import api from './api';
import { uploadAwsFile } from './uploadService';
import type { StaffMember, StaffDocument, AttendanceRecord, StaffTarget, ScheduleEntry } from '../types';

// ─── Staff CRUD ───────────────────────────────────────────────────────────────
export const fetchStaff = (params?: { role?: string; status?: string; q?: string; page?: number; limit?: number }) =>
  api.get<{ success: boolean; data: StaffMember[]; total: number }>('/staff', { params });

export const fetchStaffById = (id: string) =>
  api.get<{ success: boolean; data: StaffMember }>(`/staff/${id}`);

export const createStaff = (data: Partial<StaffMember> & { password?: string }) =>
  api.post<{ success: boolean; data: StaffMember }>('/staff', data);

export const updateStaff = (id: string, data: Partial<StaffMember> & { password?: string }) =>
  api.put<{ success: boolean; data: StaffMember }>(`/staff/${id}`, data);

export const changeStaffStatus = (id: string, action: 'activate' | 'deactivate' | 'terminate') =>
  api.patch<{ success: boolean; data: StaffMember }>(`/staff/${id}/status`, { action });

export const uploadStaffPhoto = (id: string, file: File) => {
  return uploadAwsFile(file, 'staff', { targetId: id }).then(result => ({ data: { success: true, data: result.entity as StaffMember } }));
};
export const uploadStaffDocument = (id: string, name: string, file: File) => {
  return uploadAwsFile(file, 'staff-documents', { targetId: id, title: name }).then(result => ({ data: { success: true, data: result.entity as StaffDocument[] } }));
};
export const deleteStaffDocument=(id:string,documentId:string)=>api.delete(`/staff/${id}/documents/${documentId}`);

// ─── Attendance ───────────────────────────────────────────────────────────────
export const fetchAttendance = (month: string, staffId?: string) =>
  api.get<{ success: boolean; data: AttendanceRecord[] }>('/staff/attendance', { params: { month, staffId } });

export const markAttendance = (payload: { staffId: string; date: string; type: string; checkInTime?: string; checkOutTime?: string; leaveType?: string; notes?: string }) =>
  api.post<{ success: boolean; data: AttendanceRecord }>('/staff/attendance', payload);

export const fetchStaffMonthlyAttendance = (staffId: string, month: string) =>
  api.get<{ success: boolean; data: { records: AttendanceRecord[]; summary: Record<string, number> } }>(
    `/staff/${staffId}/attendance`, { params: { month } }
  );

// ─── Schedule ─────────────────────────────────────────────────────────────────
export const fetchSchedule = (week: string) =>
  api.get<{ success: boolean; data: { weekStart: string; entries: ScheduleEntry[]; staff: StaffMember[] } }>(
    '/staff/schedule', { params: { week } }
  );

export const saveSchedule = (week: string, entries: ScheduleEntry[]) =>
  api.put<{ success: boolean; data: ScheduleEntry[] }>('/staff/schedule', { week, entries });

// ─── Targets ──────────────────────────────────────────────────────────────────
export const fetchStaffTarget = (staffId: string, month: string) =>
  api.get<{ success: boolean; data: StaffTarget }>(`/staff/${staffId}/targets/${month}`);

export const saveStaffTarget = (staffId: string, month: string, data: { revenue: number; services: number; newClients: number; productSales: number }) =>
  api.put<{ success: boolean; data: StaffTarget }>(`/staff/${staffId}/targets/${month}`, data);

export const fetchAllTargets = (month: string) =>
  api.get<{ success: boolean; data: StaffTarget[] }>('/staff/targets', { params: { month } });
